import { LockOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { DaoProjectItemType } from 'Modules'
import { useEffect, useMemo, useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import {
  DAO_DATETIME_FORMAT,
  DAO_MESSAGES,
  DAO_POOL_TYPE_LABEL,
  DaoPoolTypeEnum,
  DaoSellingVersionEnum,
} from 'constants/dao-voting'
import { usePoolFactoryContract } from 'hooks/dao-voting/use-pool-factory-contract'
import { daoTxErrorMessage } from 'hooks/dao-voting/use-prepared-tx'
import { daoApiErrorMessage } from 'modules/dao-voting/utils'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError } from 'utils/logger'

interface CreatePoolModalProps {
  open: boolean
  project: DaoProjectItemType
  poolType: DaoPoolTypeEnum
  onClose: () => void
  onSuccess: () => void
}

const WALLET_REGEX = /^0x[0-9a-fA-F]{40}$/

/**
 * Create Refund Pool / Create RWA Rewards Pool (SRS 5.9) - two steps:
 * step 1 collects inputs, step 2 confirms and opens claims. Terminal action.
 */
const CreatePoolModal = ({ open, project, poolType, onClose, onSuccess }: CreatePoolModalProps) => {
  const [form] = Form.useForm()
  const [step, setStep] = useState(1)
  const [hasDeadline, setHasDeadline] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const { address } = useAccount()
  const { deployPoolOnChain } = usePoolFactoryContract()

  const amountPerNFT = Form.useWatch('amountPerNFT', form)
  const claimOpenAt: Dayjs | undefined = Form.useWatch('claimOpenAt', form)
  const claimDeadline: Dayjs | undefined = Form.useWatch('claimDeadline', form)
  const ownerAddress = Form.useWatch('ownerAddress', form)

  const poolLabel = DAO_POOL_TYPE_LABEL[poolType]
  const sellingPrice = project.sellingPrice || 0
  const isErc3643 = project.sellingVersion === DaoSellingVersionEnum.ERC3643
  const unit = isErc3643 ? 'PTO' : 'NFT'
  /**
   * Default the admin can override in step 1. ERC-3643: the whole supply, so
   * every PTO can redeem (ASSET_SALE.md "Pool parameters"); PTO NFT: BE totalSold.
   */
  const defaultEligible = isErc3643
    ? (project.totalSupply ?? 0)
    : (project.totalSold ?? project.totalSupply ?? 0)
  let eligibleHint: string | undefined
  if (isErc3643) {
    eligibleHint = `Defaults to the total supply of ${project.totalSupply} PTOs, so every PTO can be redeemed.`
  } else if (project.totalSold != null) {
    eligibleHint = `Defaults to ${project.totalSold} NFTs sold — adjust if the eligible count differs.`
  }

  useEffect(() => {
    if (open) {
      form.resetFields()
      form.setFieldValue('totalNftsEligible', defaultEligible)
      setStep(1)
      setHasDeadline(false)
    }
  }, [open, form, defaultEligible])

  const totalToFund: number | undefined = Form.useWatch('totalToFund', form)
  const totalNftsEligible: number | undefined = Form.useWatch('totalNftsEligible', form)
  const totalEligible = totalNftsEligible && totalNftsEligible > 0 ? totalNftsEligible : 0

  const percentOfPrice = useMemo(() => {
    if (!amountPerNFT || !sellingPrice) return null
    return Math.round((amountPerNFT / sellingPrice) * 10000) / 100
  }, [amountPerNFT, sellingPrice])

  /**
   * Three-way sync (SRS 5.9): editing Amount per NFT, % of PTO Selling Price
   * or Total Amount to Fund recomputes the other two (% keeps 2 decimals).
   * % only participates when the PTO selling price is known.
   */
  const onAmountChange = (value: number | null) => {
    form.setFieldValue(
      'totalToFund',
      value && value > 0 && totalEligible > 0 ? value * totalEligible : undefined
    )
    if (sellingPrice > 0) {
      form.setFieldValue(
        'percentOfPrice',
        value && value > 0 ? Math.round((value / sellingPrice) * 10000) / 100 : undefined
      )
    }
  }

  /** spec: Amount = (% / 100) x selling price; Total = Amount x eligible NFTs */
  const onPercentChange = (value: number | null) => {
    if (sellingPrice <= 0) return
    const amount =
      value && value > 0 ? Math.round((value / 100) * sellingPrice * 100) / 100 : undefined
    form.setFieldValue('amountPerNFT', amount)
    form.setFieldValue(
      'totalToFund',
      amount && totalEligible > 0 ? amount * totalEligible : undefined
    )
  }

  /** eligible count -> Total = Amount x eligible NFTs (amount and % unchanged) */
  const onEligibleChange = (value: number | null) => {
    const amount: number | undefined = form.getFieldValue('amountPerNFT')
    form.setFieldValue(
      'totalToFund',
      amount && amount > 0 && value && value > 0 ? amount * value : undefined
    )
  }

  /** total -> amount rounded to 2 decimals, then % */
  const onTotalChange = (value: number | null) => {
    if (totalEligible <= 0) return
    const amount = value && value > 0 ? Math.round((value / totalEligible) * 100) / 100 : undefined
    form.setFieldValue('amountPerNFT', amount)
    if (sellingPrice > 0) {
      form.setFieldValue(
        'percentOfPrice',
        amount ? Math.round((amount / sellingPrice) * 10000) / 100 : undefined
      )
    }
  }

  const handleContinue = async () => {
    try {
      await form.validateFields()
      setStep(2)
    } catch {
      // validation errors are shown under the fields
    }
  }

  const handleConfirm = async () => {
    if (!claimOpenAt) return
    if (!address) {
      message.error('Please connect wallet!')
      return
    }
    try {
      setSubmitting(true)

      // 1. BE creates the pool record and prepares the deployPool calldata
      const res = await AppAdmin.createDaoVotingPool({
        tokenize: project._id,
        poolType: poolType === DaoPoolTypeEnum.REFUND ? 'REFUND' : 'RWA_REWARDS',
        amountPerNft: amountPerNFT,
        totalNftsEligible: totalEligible,
        ownerAddress: (ownerAddress || '').trim(),
        // minute-level picks - send full ISO datetime (swagger anyOf date/date-time)
        claimOpenAt: claimOpenAt.toISOString(),
        ...(hasDeadline && claimDeadline ? { claimDeadline: claimDeadline.toISOString() } : {}),
      })

      // 2. Manager wallet deploys the ClaimPool - the same tx opens the claim
      //    window on the AssetStateMachine (SRS 5.9, DAO_Usage_Guide)
      const deployCall = res.data?.data?.onChainDeployPoolCall
      if (deployCall) {
        try {
          onOpenModalProcessing()
          await deployPoolOnChain(address, deployCall)
        } catch (chainError) {
          // BE record already exists - surface the on-chain failure explicitly
          logError('Error deploying pool on-chain: ', chainError)
          message.error(daoTxErrorMessage(chainError) || 'Pool saved but on-chain deploy failed')
          onSuccess()
          onClose()
          return
        } finally {
          onCloseModalProcessing()
        }
      }

      message.success(poolType === DaoPoolTypeEnum.REFUND ? DAO_MESSAGES.S06 : DAO_MESSAGES.S07)
      if (isErc3643) {
        // ERC-3643 pools are deployed unfunded (DAO_GOVERNANCE_ERC3643_FE_NOTE 6.3)
        Modal.warning({
          title: 'Fund the pool',
          content: `The pool holds no funds yet. Fund $${Number(
            totalToFund || 0
          ).toLocaleString()} from the Pool card once it shows up - until then holders cannot claim.`,
        })
      }
      onSuccess()
      onClose()
    } catch (error: any) {
      logError('Error creating pool: ', error)
      message.error(daoApiErrorMessage(error) || 'Failed to create pool')
    } finally {
      setSubmitting(false)
    }
  }

  const stepOne = (
    <Form form={form} layout="vertical" preserve>
      <Form.Item label="Pool Type">
        <Input value={poolLabel} disabled prefix={<LockOutlined />} />
      </Form.Item>

      <Form.Item
        label={`Amount per ${unit} (USD)`}
        name="amountPerNFT"
        rules={[
          {
            validator: (_, value?: number) => {
              if (value === undefined || value === null || value <= 0) {
                return Promise.reject(new Error(DAO_MESSAGES.E06(`Amount per ${unit}`)))
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <InputNumber
          placeholder="e.g. 850"
          style={{ width: '100%' }}
          min={0}
          addonBefore="$"
          onChange={onAmountChange}
        />
      </Form.Item>

      <Form.Item
        label="% of PTO Selling Price"
        name="percentOfPrice"
        extra={
          sellingPrice > 0
            ? `PTO selling price: $${sellingPrice.toLocaleString()} per PTO — the amount, percentage, and total stay in sync`
            : 'PTO selling price unavailable — enter the amount directly.'
        }
      >
        <InputNumber
          placeholder="e.g. 85"
          style={{ width: '100%' }}
          min={0}
          precision={2}
          addonAfter="%"
          onChange={onPercentChange}
        />
      </Form.Item>

      <Form.Item
        label="Owner Address"
        name="ownerAddress"
        rules={[
          { required: true, message: 'Owner Address is required' },
          {
            validator: (_, value?: string) => {
              if (!value) return Promise.resolve()
              if (!WALLET_REGEX.test(value.trim())) {
                return Promise.reject(new Error(DAO_MESSAGES.E05))
              }
              return Promise.resolve()
            },
          },
        ]}
        extra={
          isErc3643
            ? 'Receives the leftover balance once the pool closes - it does not fund the pool.'
            : undefined
        }
      >
        <Input placeholder={isErc3643 ? '0x… treasury wallet' : '0x… wallet funding the pool'} />
      </Form.Item>

      <Form.Item
        label={`Total ${unit}s Eligible`}
        name="totalNftsEligible"
        rules={[
          {
            validator: (_, value?: number) => {
              if (value === undefined || value === null || value <= 0) {
                return Promise.reject(new Error(DAO_MESSAGES.E06(`Total ${unit}s Eligible`)))
              }
              if (!Number.isInteger(value)) {
                return Promise.reject(new Error(`Total ${unit}s Eligible must be a whole number`))
              }
              if (project.totalSupply > 0 && value > project.totalSupply) {
                return Promise.reject(
                  new Error(
                    `Total ${unit}s Eligible cannot be greater than the total supply (${project.totalSupply})`
                  )
                )
              }
              return Promise.resolve()
            },
          },
        ]}
        extra={eligibleHint}
      >
        <InputNumber
          placeholder="e.g. 100"
          style={{ width: '100%' }}
          min={1}
          precision={0}
          onChange={onEligibleChange}
        />
      </Form.Item>

      <Form.Item
        label="Total Amount to Fund"
        name="totalToFund"
        extra={
          totalEligible > 0
            ? `= Amount per ${unit} × ${totalEligible} eligible ${unit}s — the fields stay in sync.`
            : undefined
        }
      >
        <InputNumber
          placeholder="e.g. 85000"
          style={{ width: '100%' }}
          min={0}
          addonBefore="$"
          onChange={onTotalChange}
        />
      </Form.Item>

      <Form.Item
        label="Claim Open Time"
        name="claimOpenAt"
        rules={[
          { required: true, message: 'Claim Open Time is required' },
          {
            validator: (_, value?: Dayjs) => {
              if (value && value.valueOf() < Date.now()) {
                return Promise.reject(new Error('Claim open time cannot be in the past'))
              }
              return Promise.resolve()
            },
          },
        ]}
        extra="Holders can start claiming from this time."
      >
        <DatePicker
          showTime
          style={{ width: '100%' }}
          format={DAO_DATETIME_FORMAT}
          disabledDate={(d) => !!d && d.isBefore(dayjs().startOf('day'))}
        />
      </Form.Item>

      <Checkbox
        checked={hasDeadline}
        onChange={(e) => setHasDeadline(e.target.checked)}
        style={{ marginBottom: 8 }}
      >
        Set a claim deadline
      </Checkbox>
      {hasDeadline ? (
        <Form.Item
          name="claimDeadline"
          dependencies={['claimOpenAt']}
          rules={[
            { required: true, message: 'Claim deadline is required' },
            ({ getFieldValue }) => ({
              validator: (_, value?: Dayjs) => {
                if (value && value.valueOf() < Date.now()) {
                  return Promise.reject(new Error(DAO_MESSAGES.E07))
                }
                const openAt: Dayjs | undefined = getFieldValue('claimOpenAt')
                if (value && openAt && value.valueOf() <= openAt.valueOf()) {
                  return Promise.reject(
                    new Error('Claim deadline must be after the claim open time')
                  )
                }
                return Promise.resolve()
              },
            }),
          ]}
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            format={DAO_DATETIME_FORMAT}
            disabledDate={(d) => !!d && d.isBefore(dayjs().startOf('day'))}
          />
        </Form.Item>
      ) : null}
    </Form>
  )

  const stepTwo = (
    <div>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={`This action is irreversible and will open claims for ${totalEligible} eligible ${unit} holders.`}
        description={
          isErc3643
            ? `The pool is created without funds. Fund $${Number(
                totalToFund || 0
              ).toLocaleString()} from the Pool card once it is deployed - until then holders cannot claim.`
            : undefined
        }
      />
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Pool Type">{poolLabel}</Descriptions.Item>
        <Descriptions.Item label={`Amount per ${unit}`}>
          ${Number(amountPerNFT || 0).toLocaleString()}
          {percentOfPrice != null ? ` (${percentOfPrice}% of PTO price)` : ''}
        </Descriptions.Item>
        <Descriptions.Item label={`Eligible ${unit}s`}>{totalEligible}</Descriptions.Item>
        <Descriptions.Item label="Total to Fund">
          ${Number(totalToFund || 0).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="Claims Open">
          {claimOpenAt ? claimOpenAt.format(DAO_DATETIME_FORMAT) : '--'}
        </Descriptions.Item>
        <Descriptions.Item label="Claim Deadline">
          {hasDeadline && claimDeadline ? claimDeadline.format(DAO_DATETIME_FORMAT) : 'No deadline'}
        </Descriptions.Item>
        <Descriptions.Item label="Owner Address">{ownerAddress}</Descriptions.Item>
      </Descriptions>
    </div>
  )

  const footer =
    step === 1
      ? [
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button key="continue" type="primary" onClick={handleContinue}>
            Continue
          </Button>,
        ]
      : [
          <Button key="back" onClick={() => setStep(1)}>
            Back
          </Button>,
          <Button key="confirm" type="primary" loading={submitting} onClick={handleConfirm}>
            Confirm & Open Claims
          </Button>,
        ]

  return (
    <Modal
      title={`Create ${poolLabel}`}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      footer={footer}
      width={560}
      destroyOnClose
    >
      <div style={{ display: step === 1 ? 'block' : 'none' }}>{stepOne}</div>
      {step === 2 ? stepTwo : null}
    </Modal>
  )
}

export default CreatePoolModal
